import { GitIntegrationPlatformSchema } from '@terra/shared-schemas-git';
import type { z } from 'zod';

type GitIntegrationPlatformType = z.infer<typeof GitIntegrationPlatformSchema>;

type GenerateRepositoryFileUrlParams = {
  platform: GitIntegrationPlatformType;
  owner: string;
  repositoryName: string;
  filePath: string;
  branch?: string;
  baseUrl?: string | null;
  startLine?: number;
  endLine?: number;
};

function normalizeBaseUrl(base: string): string {
  return base.endsWith('/') ? base.slice(0, -1) : base;
}

export function generateRepositoryFileUrl({
  platform,
  owner,
  repositoryName,
  filePath,
  branch = 'main',
  baseUrl,
  startLine,
  endLine,
}: GenerateRepositoryFileUrlParams): string {
  const cleanFilePath = filePath.startsWith('/') ? filePath.slice(1) : filePath;
  const repoPath = `${owner}/${repositoryName}`;

  switch (platform) {
    case GitIntegrationPlatformSchema.enum.GITHUB:
    case GitIntegrationPlatformSchema.enum.GITHUB_ENTERPRISE: {
      const base = normalizeBaseUrl(baseUrl || 'https://github.com');
      let url = `${base}/${repoPath}/blob/${branch}/${cleanFilePath}`;
      if (startLine) {
        url += `#L${startLine}`;
        if (endLine && endLine !== startLine) {
          url += `-L${endLine}`;
        }
      }
      return url;
    }
    case GitIntegrationPlatformSchema.enum.GITLAB:
    case GitIntegrationPlatformSchema.enum.GITLAB_SELF_HOSTED: {
      const base = normalizeBaseUrl(baseUrl || 'https://gitlab.com');
      let url = `${base}/${repoPath}/-/blob/${branch}/${cleanFilePath}`;
      if (startLine) {
        url += `#L${startLine}`;
        if (endLine && endLine !== startLine) {
          url += `-${endLine}`;
        }
      }
      return url;
    }
    case GitIntegrationPlatformSchema.enum.BITBUCKET: {
      let url = `https://bitbucket.org/${repoPath}/src/${branch}/${cleanFilePath}`;
      if (startLine) {
        url += `#lines-${startLine}`;
      }
      return url;
    }
    default:
      return '';
  }
}
